import React, { JSX, useMemo } from 'react';
import { WarningTwoIcon } from '@chakra-ui/icons';
import { Alert, AlertIcon, Box, Grid, Heading, SimpleGrid, Stack, Text, VStack } from '@chakra-ui/react';

import { ErrorMessages, FullPageLoader, MainPageContent, PageHeading } from '../../common/components';
import { formatDate } from '../../common/utils/date.helper';
import { useTranslations } from '../../contexts/LocalizationContext';
import { useActivityMetrics, useMetricsHistory } from '../../services/monitoring';
import MetricsChart from './components/MetricsChart';
import MetricsTable from './components/MetricsTable';
import MetricTile from './components/MetricTile';
import { buildThresholdMap, METRICS, sortByWeekAsc } from './utils/metrics';

const MonitoringPage = (): JSX.Element => {
  const translations = useTranslations();
  const { data: liveMetrics, isFetching: isFetchingLive, error: liveError } = useActivityMetrics();
  const { data: history, isFetching: isFetchingHistory, error: historyError } = useMetricsHistory();

  const isFetching = isFetchingLive || isFetchingHistory;
  const error = liveError || historyError;

  const weeksAsc = useMemo(() => (history ? sortByWeekAsc(history.weeks) : []), [history]);
  const thresholdMap = useMemo(
    () => (history?.thresholds?.length ? buildThresholdMap(history.thresholds) : undefined),
    [history],
  );

  // Map live MetricResult[] by MetricKey for tile rendering
  const liveMetricsByKey = useMemo(
    () => (liveMetrics ? Object.fromEntries(liveMetrics.map((m) => [m.key, m])) : undefined),
    [liveMetrics],
  );

  return (
    <MainPageContent>
      <PageHeading>{translations('monitoring_heading')}</PageHeading>
      {error && <ErrorMessages errors={error} />}
      <FullPageLoader show={isFetching}>
        {!isFetching && !liveMetrics?.length && weeksAsc.length === 0 && (
          <VStack spacing={3} py={12}>
            <WarningTwoIcon boxSize={8} color="grey.100" />
            <Text color="grey.200">{translations('monitoring_no_weeks')}</Text>
          </VStack>
        )}
        {(liveMetricsByKey || weeksAsc.length > 0) && (
          <Stack spacing={5}>
            {!thresholdMap && weeksAsc.length > 0 && (
              <Alert status="warning" borderRadius="xl">
                <AlertIcon />
                {translations('monitoring_no_thresholds')}
              </Alert>
            )}
            {liveMetricsByKey && (
              <Box>
                <Grid templateColumns="1fr auto 1fr" alignItems="baseline" mb={4}>
                  <Box />
                  <Heading size="sm" textAlign="center">
                    {translations('monitoring_tiles_title')}
                  </Heading>
                  <Text fontSize="sm" color="grey.200" textAlign="right">
                    {translations('monitoring_last_updated', { '{{date}}': formatDate(new Date()) })}
                  </Text>
                </Grid>
                <SimpleGrid columns={{ base: 1, md: 3, lg: 5 }} spacing={4}>
                  {METRICS.map((metric) => {
                    const result = liveMetricsByKey[metric.key];
                    return result ? (
                      <MetricTile
                        key={metric.key}
                        result={result}
                        translationKey={metric.translationKey}
                        threshold={thresholdMap?.[metric.key]}
                      />
                    ) : null;
                  })}
                </SimpleGrid>
              </Box>
            )}
            {thresholdMap && <MetricsChart weeks={weeksAsc} thresholdMap={thresholdMap} />}
            {thresholdMap && <MetricsTable weeks={weeksAsc} thresholdMap={thresholdMap} />}
          </Stack>
        )}
      </FullPageLoader>
    </MainPageContent>
  );
};

export default MonitoringPage;
